import { supabase } from './supabaseClient';
import { toHttpUrl } from './url';

// The "I'm out of this" list — the step between noticing a shortage and
// raising a purchase order. Deliberately lighter than a PO: no prices, no
// line-item structure, quantity is free text.

export const REORDER_STATUSES = ['Needed', 'Ordered', 'Received', 'Dismissed'];

export async function listReorderItems() {
  const { data, error } = await supabase
    .from('reorder_items')
    .select('*, vendor:vendors(id, name, lead_time_days)')
    .order('status')
    .order('needed_by', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function addReorderItem(item) {
  const { data, error } = await supabase.from('reorder_items').insert(item).select().single();
  if (error) throw error;
  return data;
}

export async function updateReorderItem(id, updates) {
  const { data, error } = await supabase
    .from('reorder_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteReorderItem(id) {
  const { error } = await supabase.from('reorder_items').delete().eq('id', id);
  if (error) throw error;
}

// Stamps the PO that these items became, so the list records what was raised
// from what rather than the items simply disappearing.
export async function markItemsOrdered(ids, purchaseOrderId) {
  if (!ids.length) return;
  const { error } = await supabase
    .from('reorder_items')
    .update({ status: 'Ordered', purchase_order_id: purchaseOrderId })
    .in('id', ids);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Adding an item — the pure half
// ---------------------------------------------------------------------------
//
// Adding to this list used to mean filling a five-field row by hand, and the
// name/vendor/photo being typed were usually already sitting in the product
// page the user had open. So the add box takes *either* a link or a name, and
// a link is read for what it already knows. Everything below is pure so the
// guessing is testable without a browser or a network.

// Is what was typed a link, or the name of a thing?
//
// Whitespace is the strongest signal available: "Cherry Berry Flavor Oil
// 16348" is a product, "wholesalesuppliesplus.com/products/16348" is not, and
// no real URL pasted from an address bar contains a space. A dot followed by
// something TLD-shaped then separates a bare "shopname.com" from "500ml".
export function looksLikeUrl(input) {
  const raw = String(input || '').trim();
  if (!raw || /\s/.test(raw)) return false;
  if (/^https?:\/\//i.test(raw)) return true;
  // Any other scheme (javascript:, mailto:) is not a product link — treat it
  // as a name so it can never reach toHttpUrl as something clickable.
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return false;
  return /^[^\s/]+\.[a-z]{2,}(?:[/?#]|$)/i.test(raw);
}

// og:title is written for search results, not for a shopping list: shops
// routinely append their own name ("Cherry Berry Flavor Oil | Wholesale
// Supplies Plus"). Strip that tail — and the same name used as a prefix —
// so the list reads as products rather than as branding repeated ten times.
export function cleanPreviewTitle(title, siteName) {
  let out = String(title || '').replace(/\s+/g, ' ').trim();
  const site = String(siteName || '').trim();
  if (!out || !site) return out;

  const escaped = site.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const sep = '[|\\-–—·:•]';
  out = out.replace(new RegExp(`\\s*${sep}\\s*${escaped}\\s*$`, 'i'), '').trim();
  out = out.replace(new RegExp(`^\\s*${escaped}\\s*${sep}\\s*`, 'i'), '').trim();
  // A page whose entire title is the shop name tells us nothing about the
  // product; better to leave the field empty than to fill it with the vendor.
  return out.toLowerCase() === site.toLowerCase() ? '' : out;
}

// Names as typed by people vs. names as written by shops: "Wholesale Supplies
// Plus+" and "Wholesale Supplies Plus" are the same supplier, and matching
// them loosely is what stops the vendor list growing a near-duplicate every
// time a link is pasted.
function loosely(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

// The vendor a pasted link belongs to: an existing vendor's *exact stored
// name* when one matches loosely (so the autocomplete resolves it to that
// row instead of creating a twin), otherwise the site's own name to be
// created, otherwise nothing.
export function matchVendorName(vendors, siteName, url) {
  const candidates = [siteName];
  const href = toHttpUrl(url);
  if (href) candidates.push(new URL(href).hostname.replace(/^www\./, ''));

  for (const candidate of candidates) {
    const key = loosely(candidate);
    if (!key) continue;
    const hit = (vendors || []).find((v) => {
      const vendorKey = loosely(v.name);
      if (!vendorKey) return false;
      if (vendorKey === key) return true;
      // Containment, not just equality: a site calling itself "ElysianAcre
      // Soap Supplies" should still find the saved "ElysianAcre Soap", and a
      // hostname carries a ".com" the vendor name doesn't.
      //
      // Only for names long enough to be distinctive, though — a supplier
      // saved as "Soap" would otherwise claim every soap shop on the web.
      const shorter = Math.min(vendorKey.length, key.length);
      if (shorter < 6) return false;
      return key.includes(vendorKey) || vendorKey.includes(key);
    });
    if (hit) return hit.name;
  }

  return String(siteName || '').trim() || null;
}

// Two links to the same product rarely match character for character —
// ?variant=, utm tags and a trailing slash all differ between a copy from
// search results and one from the product page itself.
function linkKey(value) {
  const href = toHttpUrl(value);
  if (!href) return null;
  const url = new URL(href);
  return `${url.hostname.replace(/^www\./, '')}${url.pathname.replace(/\/$/, '')}`.toLowerCase();
}

// Is this already on the list?
//
// Only *live* items count. A received or dismissed item is finished business
// and the thing running low again is a genuinely new shortage — the same rule
// the Products page uses when deciding whether to offer "add to reorder".
//
// This warns, it never blocks: two suppliers really do sell a "silicone mould",
// and refusing the save would be wrong. But the same item landing on the list
// twice — each half-remembered, each ordered separately — is the failure this
// list actually has.
export function findExistingItem(items, { link, name } = {}) {
  const live = (items || []).filter((i) => i.status === 'Needed' || i.status === 'Ordered');

  const key = linkKey(link);
  if (key) {
    const byLink = live.find((i) => linkKey(i.link_url) === key);
    if (byLink) return byLink;
  }

  const nameKey = loosely(name);
  if (!nameKey) return null;
  return live.find((i) => loosely(i.item_name) === nameKey) || null;
}

// What a reorder item's status becomes when the purchase order carrying it
// changes status. Pure, so the rule is testable without a database.
//
// Received is terminal — the shortage is genuinely resolved. Cancelled is the
// interesting one: it returns the item to Needed rather than leaving it
// Ordered, because a cancelled order doesn't make the shortage go away, and
// an item silently stuck on "Ordered" is one you never reorder.
export function nextItemStatus(poStatus) {
  if (poStatus === 'Received') return 'Received';
  if (poStatus === 'Cancelled') return 'Needed';
  return 'Ordered'; // Ordered / Shipped — still in flight
}

// Follows a purchase order's status through to the items it was raised from.
//
// purchase_order_id is deliberately kept when an order is cancelled: it costs
// nothing, and it means un-cancelling the order puts the items back on it
// rather than orphaning them.
export async function syncItemsToOrderStatus(purchaseOrderId, poStatus) {
  if (!purchaseOrderId) return;
  const { error } = await supabase
    .from('reorder_items')
    .update({ status: nextItemStatus(poStatus) })
    .eq('purchase_order_id', purchaseOrderId)
    // Dismissed items stay dismissed — you decided you didn't need them, and
    // a delivery arriving shouldn't overrule that.
    .neq('status', 'Dismissed');
  if (error) throw error;
}
